/**
 * Search params contract for /reports. Active tab (which breakdown is
 * shown) and period mode (month/year) live in URL (TanStack Router)
 * so report is shareable/copyable: pasting `?tab=payees&period=year`
 * into another tab reconstructs exactly the same view.
 *
 * Idempotent: we omit default values (`categories` / `month`) to
 * keep URLs clean and ensure reload reconstructs state without noise.
 */

export type ReportTab =
  | "categories"
  | "trend"
  | "accounts"
  | "payees"
  | "projects"
  | "top"
  | "compare";

export type PeriodMode = "month" | "year";

export interface ReportsSearch {
  /** Active tab; `categories` is default and omitted from URL. */
  tab?: ReportTab;
  /** Period mode; `month` is the default and is omitted from the URL. */
  period?: PeriodMode;
  /**
   * Concrete navigated period: in month mode `YYYY-MM`, in year mode `YYYY`.
   * Absent = current period (omitted default). Reconstructs viewed month/year.
   */
  ref?: string;
  /** Tab Categories: kind (`income`); `expense` is the default and is omitted. */
  kind?: "income";
  /** Tab Categories: drill-down to a subcategory (`?drill=<catId>`). */
  drill?: string;
  /** Tab Compare: period A (`YYYY-MM`). */
  a?: string;
  /** Tab Compare: period B (`YYYY-MM`). */
  b?: string;
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const YEAR_RE = /^\d{4}$/;

function asMonth(value: unknown): string | undefined {
  return typeof value === "string" && MONTH_RE.test(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

const REPORT_TABS: readonly ReportTab[] = [
  "categories",
  "trend",
  "accounts",
  "payees",
  "projects",
  "top",
  "compare",
];

function asTab(value: unknown): ReportTab | undefined {
  return REPORT_TABS.find((tab) => tab === value);
}

function asPeriod(value: unknown): PeriodMode | undefined {
  return value === "month" || value === "year" ? value : undefined;
}

/**
 * Route validateSearch: discards unknown keys and invalid types,
 * and omits default values to avoid cluttering URL.
 */
export function sanitizeReportsSearch(search: Record<string, unknown>): ReportsSearch {
  const tab = asTab(search.tab);
  const period = asPeriod(search.period);
  // `ref` is interpreted according to mode: year → `YYYY`, month → `YYYY-MM`.
  const ref =
    period === "year"
      ? typeof search.ref === "string" && YEAR_RE.test(search.ref)
        ? search.ref
        : undefined
      : asMonth(search.ref);
  return {
    tab: tab === "categories" ? undefined : tab,
    period: period === "month" ? undefined : period,
    ref,
    kind: search.kind === "income" ? "income" : undefined,
    drill: asString(search.drill),
    a: asMonth(search.a),
    b: asMonth(search.b),
  };
}
